/**
 * Metadatos de los métodos de pago del módulo "Reporte de Pagos a la Aseguradora".
 *
 * Fuente de verdad del mapeo código ↔ etiqueta amigable. Si el backend incorpora un
 * método nuevo, agregarlo aquí: los códigos desconocidos caen en un fallback
 * automático y no rompen la UI.
 *
 * Los códigos coinciden EXACTAMENTE con los que acepta la API
 * (helpers/insurance-report-filters.js en kcredit-api).
 *
 * Nota: NO se exponen TDD/TDC. 'TDC' no se escribe nunca en el backend (filtraría a
 * 0 resultados) y 'TDD' es un subconjunto arbitrario de domiciliación (las
 * multi-cuota backfilleadas), no un método de pago real. DOMICILIATION las subsume.
 */

export type PaymentMethodCode =
  | 'ALL'
  | 'DOMICILIATION'
  | 'BDV_PAYMENT_BUTTON'
  | 'MERCANTIL_PAYMENT_BUTTON'
  | 'BANCO_PLAZA_INSTANT_DEBIT';

export interface PaymentMethodMeta {
  code: PaymentMethodCode | string;
  label: string;
  icon: string;
  /** Los pagos que NO son domiciliación no los procesa el cron: son de reporte manual. */
  requiresManualReport: boolean;
}

export const PAYMENT_METHOD_METADATA: { [code: string]: PaymentMethodMeta } = {
  ALL: { code: 'ALL', label: 'Todos los métodos', icon: 'fa-layer-group', requiresManualReport: false },
  DOMICILIATION: { code: 'DOMICILIATION', label: 'Domiciliación', icon: 'fa-file-invoice-dollar', requiresManualReport: false },
  BDV_PAYMENT_BUTTON: { code: 'BDV_PAYMENT_BUTTON', label: 'Botón de pagos BDV', icon: 'fa-credit-card', requiresManualReport: true },
  MERCANTIL_PAYMENT_BUTTON: { code: 'MERCANTIL_PAYMENT_BUTTON', label: 'Botón de pagos Mercantil', icon: 'fa-credit-card', requiresManualReport: true },
  BANCO_PLAZA_INSTANT_DEBIT: { code: 'BANCO_PLAZA_INSTANT_DEBIT', label: 'Débito inmediato Banco Plaza', icon: 'fa-bolt', requiresManualReport: true }
};

/** Orden de aparición en el selector. */
export const PAYMENT_METHOD_ORDER: string[] = [
  'ALL',
  'DOMICILIATION',
  'BDV_PAYMENT_BUTTON',
  'MERCANTIL_PAYMENT_BUTTON',
  'BANCO_PLAZA_INSTANT_DEBIT'
];

/** Humaniza un código desconocido: 'ALGO_NUEVO' → 'Algo Nuevo'. */
const humanize = (code: string): string =>
  (code || '').replace(/[_-]/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

export function getPaymentMethodMeta(code: string | null | undefined): PaymentMethodMeta {
  if (!code) return PAYMENT_METHOD_METADATA['ALL'];
  const found = PAYMENT_METHOD_METADATA[code];
  if (found) return found;
  return { code, label: humanize(code) || 'Método desconocido', icon: 'fa-question-circle', requiresManualReport: false };
}

export function getPaymentMethodLabel(code: string | null | undefined): string {
  return getPaymentMethodMeta(code).label;
}

/**
 * Deriva el método de pago de una fila del listado.
 *
 * La domiciliación se identifica por `typeTransaction`; el resto por
 * `insurance_report.api` (que solo existe en los pagos 'AUTO').
 */
export function derivePaymentMethodCode(payment: any): string {
  if (payment?.typeTransaction === 'DOMICILIATION') return 'DOMICILIATION';
  const api = payment?.insurance_report?.api || payment?.params?.insurance_report?.api;
  switch (api) {
    case 'BDV-PAYMENT-BUTTON': return 'BDV_PAYMENT_BUTTON';
    case 'MERCANTIL-PAYMENT-BUTTON': return 'MERCANTIL_PAYMENT_BUTTON';
    case 'BANCO_PLAZA_INSTANT_DEBIT': return 'BANCO_PLAZA_INSTANT_DEBIT';
    default: return payment?.typeTransaction === 'AUTO' ? 'AUTO' : 'DOMICILIATION';
  }
}

/**
 * ¿Este pago necesita reporte MANUAL?
 *
 * El cron de reporte automático solo procesa `typeTransaction: 'DOMICILIATION'`.
 * Los pagos 'AUTO' (botón de pagos / débito inmediato) son visibles en la consola
 * pero el automatismo NO los toma: si nadie los reporta a mano, se quedan pendientes
 * indefinidamente. La UI debe advertirlo.
 */
export function requiresManualReport(payment: any): boolean {
  return payment?.typeTransaction === 'AUTO';
}
